import { useState, useEffect, useCallback } from 'react';
import {
  Handshake, Copy, Check, Users, DollarSign, TrendingUp, ExternalLink,
  ToggleLeft, ToggleRight, Loader2, Gift, MousePointerClick, Settings2,
  Globe, Link2, Trash2, Plus, Clock, RefreshCw, Eye, EyeOff, X,
  AlertTriangle,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import {
  fetchReferralCode, generateReferralCode, updateReferralCode,
  fetchTenantReferrals, fetchReferralDashboardStats,
  fetchReferralCommissions, fetchReferralPayouts,
  fetchAffiliatePartners, upsertAffiliatePartner, deleteAffiliatePartner,
  fetchAffiliateClickStats,
  fetchTenantReferralConfig, upsertTenantReferralConfig,
  fetchUserReferralMetrics,
} from '@/lib/api';
import type { UserReferralMetrics } from '@/lib/api';
import type {
  Tenant, TenantReferralCode, TenantReferral, ReferralCommission, ReferralPayout,
  ReferralDashboardStats, TenantAffiliatePartner, AffiliateClickStats,
  TenantReferralConfig, AffiliatePartnerKey,
} from '@/types';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TABS = [
  { id: 'referrals', label: 'B2B Referrals', icon: Handshake },
  { id: 'partners', label: 'Affiliate Partners', icon: MousePointerClick },
  { id: 'user-referrals', label: 'User Referrals', icon: Gift },
] as const;
type TabId = typeof TABS[number]['id'];

interface PartnerOption {
  key: AffiliatePartnerKey;
  name: string;
  placeholder: string;
}

interface PartnerCategory {
  label: string;
  emoji: string;
  partners: PartnerOption[];
}

export const PARTNER_CATEGORIES: PartnerCategory[] = [
  {
    label: 'Accommodation & Tours',
    emoji: '🏨',
    partners: [
      { key: 'booking_com', name: 'Booking.com', placeholder: 'Your Booking.com affiliate ID' },
      { key: 'viator', name: 'Viator', placeholder: 'Your Viator partner ID' },
      { key: 'getyourguide', name: 'GetYourGuide', placeholder: 'Your GYG partner ID' },
      { key: 'tripadvisor', name: 'TripAdvisor', placeholder: 'Your TripAdvisor partner ID' },
      { key: 'hostelworld', name: 'Hostelworld', placeholder: 'Your Hostelworld affiliate ID' },
      { key: 'klook', name: 'Klook', placeholder: 'Your Klook affiliate ID' },
    ],
  },
  {
    label: 'Flights',
    emoji: '✈️',
    partners: [
      { key: 'google_flights', name: 'Google Flights', placeholder: 'Your Google affiliate ID' },
      { key: 'skyscanner', name: 'Skyscanner', placeholder: 'Your Skyscanner affiliate ID' },
      { key: 'kayak', name: 'Kayak', placeholder: 'Your Kayak partner ID' },
      { key: 'kiwi', name: 'Kiwi.com', placeholder: 'Your Kiwi.com affiliate ID' },
      { key: 'expedia', name: 'Expedia', placeholder: 'Your Expedia affiliate ID' },
      { key: 'trip_com', name: 'Trip.com', placeholder: 'Your Trip.com affiliate ID' },
    ],
  },
  {
    label: 'Transport — Rides',
    emoji: '🚗',
    partners: [
      { key: 'uber', name: 'Uber', placeholder: 'Your Uber affiliate ID' },
      { key: 'lyft', name: 'Lyft', placeholder: 'Your Lyft affiliate ID' },
      { key: 'ola', name: 'Ola', placeholder: 'Your Ola affiliate ID' },
      { key: 'bolt', name: 'Bolt', placeholder: 'Your Bolt affiliate ID' },
    ],
  },
  {
    label: 'Transport — Car Rentals',
    emoji: '🚙',
    partners: [
      { key: 'avis', name: 'Avis', placeholder: 'Your Avis affiliate ID' },
      { key: 'hertz', name: 'Hertz', placeholder: 'Your Hertz affiliate ID' },
      { key: 'enterprise', name: 'Enterprise', placeholder: 'Your Enterprise affiliate ID' },
      { key: 'sixt', name: 'Sixt', placeholder: 'Your Sixt affiliate ID' },
      { key: 'budget', name: 'Budget', placeholder: 'Your Budget affiliate ID' },
      { key: 'europcar', name: 'Europcar', placeholder: 'Your Europcar affiliate ID' },
    ],
  },
  {
    label: 'Dine — Delivery',
    emoji: '🍔',
    partners: [
      { key: 'doordash', name: 'DoorDash', placeholder: 'Your DoorDash affiliate ID' },
      { key: 'ubereats', name: 'Uber Eats', placeholder: 'Your Uber Eats affiliate ID' },
      { key: 'grubhub', name: 'Grubhub', placeholder: 'Your Grubhub affiliate ID' },
      { key: 'deliveroo', name: 'Deliveroo', placeholder: 'Your Deliveroo affiliate ID' },
      { key: 'swiggy', name: 'Swiggy', placeholder: 'Your Swiggy affiliate ID' },
      { key: 'zomato', name: 'Zomato', placeholder: 'Your Zomato affiliate ID' },
      { key: 'grabfood', name: 'Grab Food', placeholder: 'Your GrabFood affiliate ID' },
      { key: 'foodpanda', name: 'foodpanda', placeholder: 'Your foodpanda affiliate ID' },
    ],
  },
  {
    label: 'Dine — Reservations',
    emoji: '🍽️',
    partners: [
      { key: 'opentable', name: 'OpenTable', placeholder: 'Your OpenTable affiliate ID' },
      { key: 'thefork', name: 'TheFork', placeholder: 'Your TheFork affiliate ID' },
    ],
  },
  // Packing & Gear — vendors that sell travel-related products
  // (luggage, packing cubes, adapters, outdoor gear, guidebooks).
  // Used by the in-app Packing List + per-destination prep surfaces
  // to surface "Find on <Vendor>" links with the tenant's affiliate
  // tag appended. Lower commission rates than booking categories
  // (1-4%) but very high attach rate — users who'd never book a
  // hotel through us will happily click a packing-cube link.
  {
    label: 'Packing & Gear',
    emoji: '🎒',
    partners: [
      { key: 'amazon',        name: 'Amazon',         placeholder: 'Your Amazon Associates tag (e.g. yourtag-20)' },
      { key: 'rei',           name: 'REI',            placeholder: 'Your REI affiliate ID (via Avantlink)' },
      { key: 'backcountry',   name: 'Backcountry',    placeholder: 'Your Backcountry affiliate ID' },
      { key: 'patagonia',     name: 'Patagonia',      placeholder: 'Your Patagonia affiliate ID' },
      { key: 'north_face',    name: 'The North Face', placeholder: 'Your North Face affiliate ID' },
      { key: 'ebags',         name: 'eBags',          placeholder: 'Your eBags affiliate ID' },
      { key: 'travelpro',     name: 'Travelpro',      placeholder: 'Your Travelpro affiliate ID' },
      { key: 'llbean',        name: 'L.L. Bean',      placeholder: 'Your L.L.Bean affiliate ID' },
      { key: 'lonely_planet', name: 'Lonely Planet',  placeholder: 'Your Lonely Planet affiliate ID' },
      { key: 'cotopaxi',      name: 'Cotopaxi',       placeholder: 'Your Cotopaxi affiliate ID' },
    ],
  },
];

// Flattened list for backward-compat with any code referencing all options
const PARTNER_OPTIONS: PartnerOption[] = PARTNER_CATEGORIES.flatMap(c => c.partners);

// ── Main Component ─────────────────────────────────────────────────────────

export function AffiliatesPage({ tenant }: { tenant: Tenant }) {
  const [tab, setTab] = useState<TabId>('referrals');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Affiliates & Referrals</h1>
        <p className="text-sm text-surface-500 mt-1">Earn revenue through referrals, affiliate links, and user referral programs</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-surface-100 rounded-xl w-fit">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-surface-0 text-surface-900 shadow-sm border border-surface-300/60'
                  : 'text-surface-500 hover:text-surface-700'
              }`}>
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'referrals' && <B2BReferralsTab tenant={tenant} />}
      {tab === 'partners' && <AffiliatePartnersTab tenant={tenant} />}
      {tab === 'user-referrals' && <UserReferralsTab tenant={tenant} />}
    </div>
  );
}

// ── Tab 1: B2B Referrals ───────────────────────────────────────────────────

function B2BReferralsTab({ tenant }: { tenant: Tenant }) {
  const [code, setCode] = useState<TenantReferralCode | null>(null);
  const [stats, setStats] = useState<ReferralDashboardStats | null>(null);
  const [referrals, setReferrals] = useState<TenantReferral[]>([]);
  const [commissions, setCommissions] = useState<ReferralCommission[]>([]);
  const [payouts, setPayouts] = useState<ReferralPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, r, cm, p] = await Promise.all([
        fetchReferralCode(tenant.id),
        fetchReferralDashboardStats(tenant.id),
        fetchTenantReferrals(tenant.id),
        fetchReferralCommissions(tenant.id),
        fetchReferralPayouts(tenant.id),
      ]);
      setCode(c);
      setStats(s);
      setReferrals(r);
      setCommissions(cm);
      setPayouts(p);
    } catch (err) {
      console.error('Failed to load referral data:', err);
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setSaving(true);
    try {
      const c = await generateReferralCode(tenant.id);
      setCode(c);
    } finally { setSaving(false); }
  };

  const handleToggle = async () => {
    if (!code) return;
    setSaving(true);
    try {
      const updated = await updateReferralCode(code.id, { is_active: !code.is_active });
      setCode(updated);
    } finally { setSaving(false); }
  };

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRotate = async () => {
    if (!code) return;
    setRotating(true);
    try {
      const newCode = `REF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      const updated = await updateReferralCode(code.id, { referral_code: newCode });
      setCode(updated);
      setShowRotateConfirm(false);
    } catch (err) {
      console.error('Failed to rotate referral code:', err);
    } finally {
      setRotating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {/* Referral Code Card */}
      <div className="glass-panel p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-surface-900">Your Referral Code</h3>
          {code && (
            <button onClick={handleToggle} disabled={saving}
              className="flex items-center gap-2 text-sm text-surface-500 hover:text-surface-700">
              {code.is_active ? <ToggleRight size={20} className="text-green-500" /> : <ToggleLeft size={20} />}
              {code.is_active ? 'Active' : 'Inactive'}
            </button>
          )}
        </div>
        {!code ? (
          <button onClick={handleGenerate} disabled={saving}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin inline mr-2" /> : null}
            Generate Referral Code
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-surface-100 border border-surface-300 rounded-lg px-4 py-3 font-mono text-lg font-semibold text-surface-900 tracking-wider">
                {code.referral_code}
              </div>
              <button onClick={handleCopy} title="Copy code"
                className="p-3 rounded-lg border border-surface-200 hover:bg-surface-100 text-surface-600 transition-colors">
                {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
              </button>
              <button onClick={() => setShowRotateConfirm(true)} title="Rotate code"
                className="p-3 rounded-lg border border-surface-200 hover:bg-surface-100 text-surface-600 transition-colors">
                <RefreshCw size={18} />
              </button>
            </div>

            {/* Rotate confirmation */}
            {showRotateConfirm && (
              <div className="mt-3 p-4 banner-amber border rounded-xl">
                <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                  Generate a new referral code? The current code <span className="font-mono font-semibold">{code.referral_code}</span> will stop working immediately.
                  Existing referrals are not affected.
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={handleRotate} disabled={rotating}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
                    {rotating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {rotating ? 'Rotating…' : 'Yes, Rotate Code'}
                  </button>
                  <button onClick={() => setShowRotateConfirm(false)} disabled={rotating}
                    className="px-3 py-1.5 text-xs text-surface-400 hover:text-surface-300 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showRotateConfirm && (
              <p className="text-xs text-surface-500 mt-3">
                Share this code with other travel businesses. When they sign up and subscribe, you earn {code.commission_rate}% recurring commission.
              </p>
            )}
          </>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Referrals', value: stats.total_referrals, icon: Users },
            { label: 'Active', value: stats.active_referrals, icon: TrendingUp },
            { label: 'Total Earned', value: formatCents(stats.total_earned), icon: DollarSign },
            { label: 'Pending Payout', value: formatCents(Math.max(0, stats.pending_payout)), icon: DollarSign },
          ].map(s => (
            <div key={s.label} className="glass-panel p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={14} className="text-surface-400" />
                <span className="text-xs text-surface-500">{s.label}</span>
              </div>
              <div className="text-xl font-bold text-surface-900">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Referrals Table */}
      {referrals.length > 0 && (
        <div className="glass-panel overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-300">
            <h3 className="font-semibold text-surface-900">Referred Tenants</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-surface-300/60 text-left text-surface-500">
                <th className="px-5 py-3 font-medium">Tenant</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {referrals.map(r => (
                  <tr key={r.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                    <td className="px-5 py-3 font-medium text-surface-900">{r.referred_tenant?.name || '—'}</td>
                    <td className="px-5 py-3 text-surface-500">{formatDate(r.referred_at)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.status === 'active' ? 'bg-green-900/30 text-green-400' :
                        r.status === 'pending' ? 'badge-amber' :
                        'bg-surface-300/30 text-surface-500'
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commissions Table */}
      {commissions.length > 0 && (
        <div className="glass-panel overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-300">
            <h3 className="font-semibold text-surface-900">Commissions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-surface-300/60 text-left text-surface-500">
                <th className="px-5 py-3 font-medium">Period</th>
                <th className="px-5 py-3 font-medium">Subscription</th>
                <th className="px-5 py-3 font-medium">Rate</th>
                <th className="px-5 py-3 font-medium">Commission</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {commissions.map(c => (
                  <tr key={c.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                    <td className="px-5 py-3 text-surface-500">{c.period_start ? formatDate(c.period_start) : '—'}</td>
                    <td className="px-5 py-3 text-surface-900">{formatCents(c.subscription_amount)}</td>
                    <td className="px-5 py-3 text-surface-500">{c.commission_rate}%</td>
                    <td className="px-5 py-3 font-semibold text-green-600">{formatCents(c.commission_amount)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.status === 'paid' ? 'bg-green-900/30 text-green-400' :
                        c.status === 'earned' ? 'badge-blue' :
                        'bg-surface-300/30 text-surface-500'
                      }`}>{c.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payouts Table */}
      {payouts.length > 0 && (
        <div className="glass-panel overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-300">
            <h3 className="font-semibold text-surface-900">Payouts</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-surface-300/60 text-left text-surface-500">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Method</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                    <td className="px-5 py-3 text-surface-500">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3 font-semibold text-surface-900">{formatCents(p.amount)}</td>
                    <td className="px-5 py-3 text-surface-500 capitalize">{p.payout_method.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.status === 'paid' ? 'bg-green-900/30 text-green-400' :
                        p.status === 'processing' ? 'badge-blue' :
                        p.status === 'pending' ? 'badge-amber' :
                        'bg-red-100 text-red-700'
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {referrals.length === 0 && !loading && (
        <div className="text-center py-12 text-surface-500">
          <Handshake size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No referrals yet. Share your code to start earning!</p>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Affiliate Partners ──────────────────────────────────────────────

function AffiliatePartnersTab({ tenant }: { tenant: Tenant }) {
  const [partners, setPartners] = useState<TenantAffiliatePartner[]>([]);
  const [clickStats, setClickStats] = useState<AffiliateClickStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Per-partner reveal toggle. Affiliate IDs are treated like
  // passwords on this screen — masked by default so shoulder-surfers
  // / screen-recordings / over-the-shoulder admin sessions don't leak
  // tenant tracking codes. The user can click the eye icon to
  // temporarily reveal one ID at a time. State is per-row so revealing
  // one doesn't expose the rest.
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        fetchAffiliatePartners(tenant.id),
        fetchAffiliateClickStats(tenant.id),
      ]);
      setPartners(p);
      setClickStats(s);
      // Init drafts from existing partners
      const d: Record<string, string> = {};
      p.forEach(pt => { d[pt.partner_key] = pt.affiliate_id; });
      setDrafts(d);
    } catch (err) {
      console.error('Failed to load affiliate partners:', err);
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (key: string, name: string) => {
    const affiliateId = drafts[key]?.trim();
    if (!affiliateId) return;
    setSaving(key);
    try {
      await upsertAffiliatePartner(tenant.id, { partner_key: key, partner_name: name, affiliate_id: affiliateId, is_active: true });
      await load();
    } finally { setSaving(null); }
  };

  const handleRemove = async (partner: TenantAffiliatePartner) => {
    setSaving(partner.partner_key);
    try {
      await deleteAffiliatePartner(partner.id, tenant.id);
      setDrafts(d => { const n = { ...d }; delete n[partner.partner_key]; return n; });
      await load();
    } finally { setSaving(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  const existingKeys = new Set(partners.map(p => p.partner_key));

  return (
    <div className="space-y-6">
      {/* Click Stats */}
      {clickStats && clickStats.total_clicks > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Clicks', value: clickStats.total_clicks },
            { label: 'Today', value: clickStats.clicks_today },
            { label: 'This Week', value: clickStats.clicks_this_week },
            { label: 'This Month', value: clickStats.clicks_this_month },
          ].map(s => (
            <div key={s.label} className="glass-panel p-4">
              <div className="text-xs text-surface-500 mb-1">{s.label}</div>
              <div className="text-xl font-bold text-surface-900">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-5">
        <div className="glass-panel p-5">
          <h3 className="font-semibold text-surface-900 mb-1">Booking Partner Affiliate IDs</h3>
          <p className="text-xs text-surface-500">
            Enter your affiliate/partner IDs for each booking platform. When users click booking links in your app, your affiliate tracking will be automatically appended.
          </p>
        </div>

        {PARTNER_CATEGORIES.map(cat => (
          <div key={cat.label} className="glass-panel p-6">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-surface-800 mb-4">
              <span>{cat.emoji}</span> {cat.label}
            </h4>
            <div className="space-y-3">
              {cat.partners.map(opt => {
                const existing = partners.find(p => p.partner_key === opt.key);
                const draft = drafts[opt.key] || '';
                const isChanged = existing ? draft !== existing.affiliate_id : draft.trim().length > 0;

                // Reveal the input as plain text only when:
                //   (a) the user explicitly toggled the eye for this row, OR
                //   (b) the field is empty (nothing to mask anyway — let
                //       the user see what they're typing in for the
                //       first time)
                // Otherwise render as type=password so the stored ID
                // shows up as •••• and isn't readable at a glance.
                const isRevealed   = !!revealed[opt.key];
                const inputIsEmpty = draft.trim().length === 0;
                const showAsText   = isRevealed || inputIsEmpty;
                return (
                  <div key={opt.key} className="flex items-center gap-3 p-3 rounded-lg border border-surface-300/60 hover:border-surface-300 transition-colors">
                    <div className="w-32 shrink-0">
                      <div className="text-sm font-medium text-surface-900">{opt.name}</div>
                      {existing && (
                        <span className={`text-[10px] font-medium ${existing.is_active ? 'text-green-600' : 'text-surface-400'}`}>
                          {existing.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type={showAsText ? 'text' : 'password'}
                        placeholder={opt.placeholder}
                        value={draft}
                        onChange={e => setDrafts(d => ({ ...d, [opt.key]: e.target.value }))}
                        // autoComplete=off so the browser doesn't try to
                        // save these alongside the admin's own passwords.
                        // 'new-password' is the canonical opt-out string.
                        autoComplete="new-password"
                        className="w-full px-3 py-2 pr-10 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 placeholder-surface-500 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10"
                      />
                      {/* Eye toggle — hidden when the field is empty
                          (no point offering reveal on nothing). */}
                      {!inputIsEmpty && (
                        <button
                          type="button"
                          onClick={() => setRevealed(r => ({ ...r, [opt.key]: !r[opt.key] }))}
                          aria-label={isRevealed ? `Hide ${opt.name} affiliate ID` : `Show ${opt.name} affiliate ID`}
                          title={isRevealed ? 'Hide affiliate ID' : 'Show affiliate ID'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-surface-400 hover:text-surface-700 transition-colors"
                        >
                          {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                    {isChanged && draft.trim() && (
                      <button onClick={() => handleSave(opt.key, opt.name)} disabled={saving === opt.key}
                        className="px-3 py-2 bg-brand-500 text-white rounded-lg text-xs font-medium hover:bg-brand-600 disabled:opacity-50">
                        {saving === opt.key ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                      </button>
                    )}
                    {existing && (
                      <button onClick={() => handleRemove(existing)} disabled={saving === opt.key}
                        className="p-2 text-surface-400 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Click Breakdown */}
      {clickStats && clickStats.by_partner.length > 0 && (
        <div className="glass-panel p-6">
          <h3 className="font-semibold text-surface-900 mb-4">Click Breakdown by Partner</h3>
          <div className="space-y-3">
            {clickStats.by_partner.map(bp => {
              const pct = clickStats.total_clicks > 0 ? (bp.clicks / clickStats.total_clicks) * 100 : 0;
              return (
                <div key={bp.partner_key} className="flex items-center gap-3">
                  <div className="w-28 text-sm text-surface-700 capitalize">{bp.partner_name}</div>
                  <div className="flex-1 h-2 bg-surface-300/30 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-12 text-right text-sm font-medium text-surface-900">{bp.clicks}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {partners.length === 0 && !loading && (
        <div className="text-center py-8 text-surface-500">
          <MousePointerClick size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No affiliate partners configured yet. Add your partner IDs above to start tracking.</p>
        </div>
      )}
    </div>
  );
}

// ── Tab 3: User Referrals ──────────────────────────────────────────────────

function UserReferralsTab({ tenant }: { tenant: Tenant }) {
  const [config, setConfig] = useState<TenantReferralConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState<UserReferralMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [form, setForm] = useState({
    is_enabled: false,
    reward_type: 'none' as string,
    reward_value: 0,
    reward_description: '',
    referrer_reward_type: 'none' as string,
    referrer_reward_value: 0,
    referrer_reward_description: '',
    referrer_reward_tiers: null as { tiers: any[]; milestones: any[] } | null,
    max_referrals_per_user: 0,   // 0 = unlimited
    reward_trigger: 'immediate' as 'immediate' | 'after_first_trip',
  });
  const [referrerMode, setReferrerMode] = useState<'simple' | 'tiered'>('simple');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await fetchTenantReferralConfig(tenant.id);
      setConfig(c);
      if (c) {
        const tiers = (c as any).referrer_reward_tiers as { tiers: any[]; milestones: any[] } | null;
        setForm({
          is_enabled: c.is_enabled,
          reward_type: c.reward_type,
          reward_value: c.reward_value,
          reward_description: c.reward_description || '',
          referrer_reward_type: c.referrer_reward_type,
          referrer_reward_value: c.referrer_reward_value,
          referrer_reward_description: c.referrer_reward_description || '',
          referrer_reward_tiers: tiers || null,
          max_referrals_per_user: c.max_referrals_per_user,
          reward_trigger: (c as any).reward_trigger || 'immediate',
        });
        setReferrerMode(tiers && tiers.tiers.length > 0 ? 'tiered' : 'simple');
      }
    } catch (err) {
      console.error('Failed to load referral config:', err);
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const data = await fetchUserReferralMetrics(tenant.id);
      setMetrics(data);
    } catch (err) {
      console.error('Failed to load referral metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); loadMetrics(); }, [load, loadMetrics]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // In tiered mode, clear flat referrer fields (they're replaced by tiers)
      if (referrerMode === 'tiered') {
        payload.referrer_reward_type = 'none';
        payload.referrer_reward_value = 0;
        payload.referrer_reward_description = '';
      } else {
        // In simple mode, clear tiers
        payload.referrer_reward_tiers = null;
      }
      const updated = await upsertTenantReferralConfig(tenant.id, payload as any);
      setConfig(updated);
    } finally { setSaving(false); }
  };

  const set = (key: string, val: any) => setForm(f => ({ ...f, [key]: val }));

  // Reward types whose fulfillment in process-referral-rewards/index.ts
  // currently RECORDS the reward in user_reward_ledger but does NOT
  // automatically apply it to the user's bill. Whoever runs billing
  // has to read the ledger and apply the credit/discount themselves
  // at the next invoice. The amber banner below the form surfaces this
  // when an admin picks one of these. free_days / free_month /
  // plan_upgrade all fulfill end-to-end against user_subscriptions and
  // do NOT carry the warning.
  const MANUAL_FULFILLMENT_TYPES = ['discount_pct', 'credit'] as const;
  const labelWithStatus = (value: string, base: string) =>
    (MANUAL_FULFILLMENT_TYPES as readonly string[]).includes(value)
      ? `${base} — manual fulfillment`
      : base;

  const REWARD_TYPES = [
    { value: 'none', label: 'No Reward' },
    { value: 'free_month', label: 'Free Month(s)' },
    { value: 'free_days', label: 'Free Days' },
    { value: 'discount_pct', label: labelWithStatus('discount_pct', 'Discount %') },
    { value: 'credit', label: labelWithStatus('credit', 'Account Credit') },
  ];

  const TIER_REWARD_TYPES = [
    { value: 'free_days', label: 'Free Days' },
    { value: 'free_month', label: 'Free Month(s)' },
    { value: 'discount_pct', label: labelWithStatus('discount_pct', 'Discount %') },
    { value: 'credit', label: labelWithStatus('credit', 'Account Credit') },
    { value: 'plan_upgrade', label: 'Plan Upgrade' },
  ];

  const BADGE_EMOJIS = [
    { value: 'star', label: '⭐' }, { value: 'rocket', label: '🚀' },
    { value: 'trophy', label: '🏆' }, { value: 'target', label: '🎯' },
    { value: 'gem', label: '💎' }, { value: 'fire', label: '🔥' },
    { value: 'crown', label: '👑' }, { value: 'medal', label: '🏅' },
  ];

  /** Returns the appropriate label for the "value" input based on reward type */
  const rewardValueLabel = (type: string) => {
    switch (type) {
      case 'free_month': return 'Months';
      case 'free_days': return 'Days';
      case 'discount_pct': return 'Percentage';
      case 'credit': return 'Amount (cents)';
      case 'plan_upgrade': return 'Months of upgrade';
      default: return 'Value';
    }
  };

  // ── Tier helpers ──
  const ensureTiers = () => {
    if (!form.referrer_reward_tiers) {
      set('referrer_reward_tiers', { tiers: [], milestones: [] });
    }
  };

  const addTier = () => {
    ensureTiers();
    const tiers = form.referrer_reward_tiers?.tiers || [];
    const lastMax = tiers.length > 0 ? (tiers[tiers.length - 1].max_referrals || 0) : 0;
    const newTier = {
      id: `tier-${Date.now()}`,
      name: '',
      min_referrals: lastMax + 1,
      max_referrals: lastMax + 10,
      reward_type: 'free_days',
      reward_value: 7,
      description: '',
    };
    set('referrer_reward_tiers', {
      tiers: [...(form.referrer_reward_tiers?.tiers || []), newTier],
      milestones: form.referrer_reward_tiers?.milestones || [],
    });
  };

  const updateTier = (idx: number, field: string, val: any) => {
    const tiers = [...(form.referrer_reward_tiers?.tiers || [])];
    tiers[idx] = { ...tiers[idx], [field]: val };
    set('referrer_reward_tiers', { ...form.referrer_reward_tiers!, tiers });
  };

  const removeTier = (idx: number) => {
    const tiers = (form.referrer_reward_tiers?.tiers || []).filter((_: any, i: number) => i !== idx);
    set('referrer_reward_tiers', { ...form.referrer_reward_tiers!, tiers });
  };

  const addMilestone = () => {
    ensureTiers();
    const milestones = form.referrer_reward_tiers?.milestones || [];
    const lastAt = milestones.length > 0 ? milestones[milestones.length - 1].at_referrals : 0;
    const newMs = {
      id: `ms-${Date.now()}`,
      at_referrals: lastAt + 5,
      reward_type: 'free_month',
      reward_value: 1,
      name: '',
      description: '',
      badge_emoji: 'star',
    };
    set('referrer_reward_tiers', {
      tiers: form.referrer_reward_tiers?.tiers || [],
      milestones: [...milestones, newMs],
    });
  };

  const updateMilestone = (idx: number, field: string, val: any) => {
    const milestones = [...(form.referrer_reward_tiers?.milestones || [])];
    milestones[idx] = { ...milestones[idx], [field]: val };
    set('referrer_reward_tiers', { ...form.referrer_reward_tiers!, milestones });
  };

  const removeMilestone = (idx: number) => {
    const milestones = (form.referrer_reward_tiers?.milestones || []).filter((_: any, i: number) => i !== idx);
    set('referrer_reward_tiers', { ...form.referrer_reward_tiers!, milestones });
  };

  // True if ANY of the 4 places where reward_type can be set is
  // currently using a manual-fulfillment type (discount_pct / credit).
  // Used to gate the amber warning banner that explains those types
  // are recorded but not auto-applied to the user's bill.
  const isManual = (t?: string) =>
    !!t && (MANUAL_FULFILLMENT_TYPES as readonly string[]).includes(t);
  const hasManualFulfillmentType =
    isManual(form.reward_type) ||
    isManual(form.referrer_reward_type) ||
    (form.referrer_reward_tiers?.tiers || []).some((t: any) => isManual(t?.reward_type)) ||
    (form.referrer_reward_tiers?.milestones || []).some((m: any) => isManual(m?.reward_type));

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="glass-panel p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-surface-900">User Referral Program</h3>
            <p className="text-xs text-surface-500 mt-1">Let your app users refer friends and earn rewards</p>
          </div>
          <button onClick={() => set('is_enabled', !form.is_enabled)}
            className="flex items-center gap-2 text-sm">
            {form.is_enabled
              ? <ToggleRight size={24} className="text-green-500" />
              : <ToggleLeft size={24} className="text-surface-400" />}
            <span className={form.is_enabled ? 'text-green-600 font-medium' : 'text-surface-500'}>
              {form.is_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
        </div>

        {form.is_enabled && (
          <div className="space-y-6">
            {/* Manual-fulfillment warning. Surfaces only when at least one
                selected reward type is Discount % or Account Credit —
                fulfillment for those is recorded in user_reward_ledger
                but NOT auto-applied to the user's invoice. See
                supabase/functions/process-referral-rewards/index.ts for
                why: only free_days, free_month and plan_upgrade write to
                user_subscriptions; discount_pct and credit stop at the
                ledger row. Until a billing integration ships, whoever
                runs invoicing has to read the ledger and apply each
                pending entry by hand. We don't BLOCK saving — operators
                might genuinely want to record these for manual fulfillment
                — we just make sure no one picks them by mistake. */}
            {hasManualFulfillmentType && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <div className="font-semibold mb-1">Manual fulfillment required</div>
                  <p className="leading-relaxed">
                    One or more selected reward types use <strong>manual fulfillment</strong>.
                    {' '}<strong>Discount %</strong> and <strong>Account Credit</strong> are
                    recorded in <code className="text-[12px] bg-amber-100 px-1 rounded">user_reward_ledger</code>{' '}
                    but are <strong>not automatically applied</strong> to the user's bill. Until the
                    billing integration ships, you (or whoever runs billing) must read the ledger
                    and apply the credit/discount manually at the next invoice.
                  </p>
                  <p className="leading-relaxed mt-2">
                    <strong>Free Days</strong>, <strong>Free Month(s)</strong>, and <strong>Plan Upgrade</strong>{' '}
                    fulfill end-to-end automatically against <code className="text-[12px] bg-amber-100 px-1 rounded">user_subscriptions</code>.
                  </p>
                </div>
              </div>
            )}

            {/* Reward Trigger */}
            <div className="p-4 bg-surface-100 rounded-lg border border-surface-300/60">
              <div className="text-sm font-medium text-surface-900 mb-3 flex items-center gap-2">
                <Clock size={14} /> Reward Trigger
              </div>
              <p className="text-xs text-surface-500 mb-3">When should referral rewards be applied?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => set('reward_trigger', 'immediate')}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    form.reward_trigger === 'immediate'
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-surface-100 text-surface-700 border-surface-300 hover:border-brand-400'
                  }`}
                >
                  Immediate (on signup)
                </button>
                <button
                  onClick={() => set('reward_trigger', 'after_first_trip')}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    form.reward_trigger === 'after_first_trip'
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'bg-surface-100 text-surface-700 border-surface-300 hover:border-brand-400'
                  }`}
                >
                  After first trip
                </button>
              </div>
              {form.reward_trigger === 'after_first_trip' && (
                <p className="text-[11px] text-amber-600 mt-2">
                  Rewards will stay pending until the referred user creates their first trip. This reduces fraud but delays gratification.
                </p>
              )}
            </div>

            {/* Referred user reward */}
            <div className="p-4 bg-surface-100 rounded-lg border border-surface-300/60">
              <div className="text-sm font-medium text-surface-900 mb-3 flex items-center gap-2">
                <Gift size={14} /> New User Reward (person who signs up)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-surface-500 mb-1 block">Reward Type</label>
                  <select value={form.reward_type} onChange={e => set('reward_type', e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500">
                    {REWARD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                  </select>
                </div>
                {form.reward_type !== 'none' && (
                  <div>
                    <label className="text-xs text-surface-500 mb-1 block">
                      {rewardValueLabel(form.reward_type)}
                    </label>
                    <input type="number" value={form.reward_value} onChange={e => set('reward_value', Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500" />
                  </div>
                )}
                {form.reward_type !== 'none' && (
                  <div>
                    <label className="text-xs text-surface-500 mb-1 block">Description</label>
                    <input type="text" value={form.reward_description} onChange={e => set('reward_description', e.target.value)}
                      placeholder="e.g., Get 1 free month!"
                      className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Referrer reward — Simple or Tiered mode */}
            <div className="p-4 bg-surface-100 rounded-lg border border-surface-300/60">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-surface-900 flex items-center gap-2">
                  <Users size={14} /> Referrer Reward (person who refers)
                </div>
                <div className="flex bg-surface-100 rounded-lg p-0.5 text-xs">
                  <button onClick={() => setReferrerMode('simple')}
                    className={`px-3 py-1.5 rounded-md transition-all ${referrerMode === 'simple' ? 'bg-surface-0 shadow-sm font-semibold text-surface-900 border border-surface-300/60' : 'text-surface-500 hover:text-surface-700'}`}>
                    Simple
                  </button>
                  <button onClick={() => { setReferrerMode('tiered'); ensureTiers(); }}
                    className={`px-3 py-1.5 rounded-md transition-all ${referrerMode === 'tiered' ? 'bg-surface-0 shadow-sm font-semibold text-surface-900 border border-surface-300/60' : 'text-surface-500 hover:text-surface-700'}`}>
                    Tiered
                  </button>
                </div>
              </div>

              {referrerMode === 'simple' ? (
                /* ── Simple (flat) referrer reward ── */
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-surface-500 mb-1 block">Reward Type</label>
                    <select value={form.referrer_reward_type} onChange={e => set('referrer_reward_type', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500">
                      {REWARD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                    </select>
                  </div>
                  {form.referrer_reward_type !== 'none' && (
                    <div>
                      <label className="text-xs text-surface-500 mb-1 block">{rewardValueLabel(form.referrer_reward_type)}</label>
                      <input type="number" value={form.referrer_reward_value} onChange={e => set('referrer_reward_value', Number(e.target.value))}
                        className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500" />
                    </div>
                  )}
                  {form.referrer_reward_type !== 'none' && (
                    <div>
                      <label className="text-xs text-surface-500 mb-1 block">Description</label>
                      <input type="text" value={form.referrer_reward_description} onChange={e => set('referrer_reward_description', e.target.value)}
                        placeholder="e.g., Earn $5 credit per referral"
                        className="w-full px-3 py-2 text-sm bg-surface-100 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500" />
                    </div>
                  )}
                </div>
              ) : (
                /* ── Tiered referrer rewards ── */
                <div className="space-y-4">
                  {/* Reward Tiers */}
                  <div>
                    <div className="text-xs font-semibold text-surface-600 uppercase tracking-wide mb-2">Reward Tiers</div>
                    <p className="text-xs text-surface-400 mb-3">Define escalating rewards based on referral count. Higher tiers = better rewards to motivate power referrers.</p>
                    {(form.referrer_reward_tiers?.tiers || []).map((tier: any, idx: number) => (
                      <div key={tier.id} className="grid grid-cols-12 gap-2 mb-2 items-end">
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Name</label>}
                          <input type="text" value={tier.name} onChange={e => updateTier(idx, 'name', e.target.value)}
                            placeholder="e.g., Starter"
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Min</label>}
                          <input type="number" min={1} value={tier.min_referrals} onChange={e => updateTier(idx, 'min_referrals', Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Max</label>}
                          <input type="number" min={0} value={tier.max_referrals || ''} onChange={e => updateTier(idx, 'max_referrals', e.target.value ? Number(e.target.value) : null)}
                            placeholder="∞"
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Reward</label>}
                          <select value={tier.reward_type} onChange={e => updateTier(idx, 'reward_type', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20">
                            {TIER_REWARD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Value</label>}
                          <input type="number" min={0} value={tier.reward_value} onChange={e => updateTier(idx, 'reward_value', Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-4">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Description</label>}
                          <input type="text" value={tier.description} onChange={e => updateTier(idx, 'description', e.target.value)}
                            placeholder="e.g., 7 free days per referral"
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">&nbsp;</label>}
                          <button onClick={() => removeTier(idx)} className="p-1 text-red-600 dark:text-red-400 hover:text-red-600 transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button onClick={addTier}
                      className="text-xs text-brand-500 hover:text-brand-700 font-medium flex items-center gap-1 mt-1">
                      + Add Tier
                    </button>
                  </div>

                  {/* Milestones */}
                  <div className="border-t border-surface-300/60 pt-4">
                    <div className="text-xs font-semibold text-surface-600 uppercase tracking-wide mb-2">Milestone Bonuses</div>
                    <p className="text-xs text-surface-400 mb-3">One-time bonus rewards at specific referral counts. Great for celebrating achievements!</p>
                    {(form.referrer_reward_tiers?.milestones || []).map((ms: any, idx: number) => (
                      <div key={ms.id} className="grid grid-cols-12 gap-2 mb-2 items-end">
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Badge</label>}
                          <select value={ms.badge_emoji} onChange={e => updateMilestone(idx, 'badge_emoji', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20">
                            {BADGE_EMOJIS.map(be => <option key={be.value} value={be.value}>{be.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Name</label>}
                          <input type="text" value={ms.name} onChange={e => updateMilestone(idx, 'name', e.target.value)}
                            placeholder="e.g., First Five"
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">At #</label>}
                          <input type="number" min={1} value={ms.at_referrals} onChange={e => updateMilestone(idx, 'at_referrals', Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Reward</label>}
                          <select value={ms.reward_type} onChange={e => updateMilestone(idx, 'reward_type', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20">
                            {TIER_REWARD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Value</label>}
                          <input type="number" min={0} value={ms.reward_value} onChange={e => updateMilestone(idx, 'reward_value', Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 text-center focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-4">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">Description</label>}
                          <input type="text" value={ms.description} onChange={e => updateMilestone(idx, 'description', e.target.value)}
                            placeholder="e.g., Bonus: 1 free month!"
                            className="w-full px-2 py-1.5 text-xs bg-surface-50 border border-surface-300 rounded-lg text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          {idx === 0 && <label className="text-xs text-surface-500 mb-1 block">&nbsp;</label>}
                          <button onClick={() => removeMilestone(idx)} className="p-1 text-red-600 dark:text-red-400 hover:text-red-600 transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button onClick={addMilestone}
                      className="text-xs text-brand-500 hover:text-brand-700 font-medium flex items-center gap-1 mt-1">
                      + Add Milestone
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Max referrals */}
            <div className="flex items-center gap-4">
              <label className="text-sm text-surface-700">Max referrals per user</label>
              <input type="number" min={0} max={500} value={form.max_referrals_per_user}
                onChange={e => set('max_referrals_per_user', Math.max(0, Number(e.target.value)))}
                className="w-20 px-3 py-2 text-sm bg-surface-50 border border-surface-300 rounded-lg text-center text-surface-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
              <span className="text-xs text-surface-400">(0 = unlimited)</span>
            </div>

            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2.5 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin inline mr-2" /> : null}
              Save Configuration
            </button>
          </div>
        )}
      </div>

      {/* ── Referral Metrics Dashboard ── */}
      {form.is_enabled && (
        <div className="glass-panel p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-semibold text-surface-900">Referral Metrics</h3>
              <p className="text-xs text-surface-500 mt-1">Track how your users are referring friends</p>
            </div>
            <button
              onClick={loadMetrics}
              disabled={metricsLoading}
              className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-300 transition-colors disabled:opacity-50"
            >
              {metricsLoading ? <Loader2 size={12} className="animate-spin" /> : 'Refresh'}
            </button>
          </div>

          {metricsLoading && !metrics ? (
            <div className="flex justify-center py-8"><Spinner size="md" /></div>
          ) : !metrics ? (
            <p className="text-sm text-surface-500 text-center py-6">
              Unable to load metrics. Make sure the tenant-referrals edge function is deployed.
            </p>
          ) : (
            <div className="space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-surface-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-surface-900">{metrics.stats.total_codes}</div>
                  <div className="text-xs text-surface-500 mt-1">Active Referrers</div>
                </div>
                <div className="bg-surface-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-brand-600 dark:text-brand-400">{metrics.stats.total_referrals}</div>
                  <div className="text-xs text-surface-500 mt-1">Total Referrals</div>
                </div>
                <div className="bg-surface-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{metrics.stats.pending_referrals}</div>
                  <div className="text-xs text-surface-500 mt-1">Pending</div>
                </div>
                <div className="bg-surface-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400">{metrics.stats.completed_referrals}</div>
                  <div className="text-xs text-surface-500 mt-1">Completed</div>
                </div>
              </div>

              {/* Top Referrers table */}
              {metrics.codes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-surface-700 mb-3 flex items-center gap-2">
                    <TrendingUp size={14} /> Top Referrers
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-surface-300/60 text-left">
                          <th className="pb-2 text-xs font-medium text-surface-500">User</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Code</th>
                          <th className="pb-2 text-xs font-medium text-surface-500 text-center">Referrals</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Status</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.codes.map(code => (
                          <tr key={code.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                            <td className="py-2.5">
                              <div className="text-surface-900 font-medium">{code.referrer_name || '—'}</div>
                              <div className="text-xs text-surface-500">{code.referrer_email}</div>
                            </td>
                            <td className="py-2.5 font-mono text-xs text-surface-700">{code.referral_code}</td>
                            <td className="py-2.5 text-center font-semibold text-surface-900">{code.total_referrals}</td>
                            <td className="py-2.5">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                code.is_active
                                  ? 'bg-green-900/30 text-green-400'
                                  : 'bg-surface-300/30 text-surface-500'
                              }`}>
                                {code.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-2.5 text-xs text-surface-500">{formatDate(code.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Referrals table */}
              {metrics.referrals.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-surface-700 mb-3 flex items-center gap-2">
                    <Users size={14} /> Recent Referrals
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-surface-300/60 text-left">
                          <th className="pb-2 text-xs font-medium text-surface-500">Referred By</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">New User</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Code Used</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Status</th>
                          <th className="pb-2 text-xs font-medium text-surface-500">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.referrals.map(ref => (
                          <tr key={ref.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                            <td className="py-2.5">
                              <div className="text-surface-900 font-medium">{ref.referrer_name || '—'}</div>
                              <div className="text-xs text-surface-500">{ref.referrer_email}</div>
                            </td>
                            <td className="py-2.5">
                              <div className="text-surface-900 font-medium">{ref.referred_name || '—'}</div>
                              <div className="text-xs text-surface-500">{ref.referred_email}</div>
                            </td>
                            <td className="py-2.5 font-mono text-xs text-surface-700">{ref.referral_code_used}</td>
                            <td className="py-2.5">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                ref.status === 'completed' || ref.status === 'rewarded'
                                  ? 'bg-green-900/30 text-green-400'
                                  : ref.status === 'pending'
                                    ? 'badge-amber'
                                    : 'bg-surface-300/30 text-surface-500'
                              }`}>
                                {ref.status.charAt(0).toUpperCase() + ref.status.slice(1)}
                              </span>
                            </td>
                            <td className="py-2.5 text-xs text-surface-500">{formatDate(ref.referred_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {metrics.referrals.length === 0 && metrics.codes.length === 0 && (
                <p className="text-sm text-surface-500 text-center py-4">
                  No referrals yet. Once users generate codes and refer friends, their activity will appear here.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
